/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { paragraphXSmall, weightSemiBold } from 'src/components/css'

export type StatusTone = 'ok' | 'warn' | 'error' | 'info' | 'muted'

interface StatusChipProps {
  tone: StatusTone
  children: ReactNode
}

const TONES: Record<StatusTone, { fg: string; bg: string }> = {
  ok: { fg: colors.SUCCESS_200, bg: `${colors.SUCCESS_300}24` },
  warn: { fg: colors.WARNING_300, bg: `${colors.WARNING_300}24` },
  error: { fg: colors.ERROR_100, bg: `${colors.ERROR_300}29` },
  info: { fg: colors.INFO_300, bg: `${colors.INFO_300}24` },
  muted: { fg: colors.NEUTRAL_50, bg: colors.SECONDARY_300 },
}

/**
 * A compact state chip: a tinted, toned label with a leading dot. Semantic
 * colour only — never the brand accent, which is reserved for selection and
 * primary actions.
 *
 * Not to be confused with `StatusPill` (a filled, icon + label pill coloured by
 * the caller) — the two share a purpose but not a look.
 */
const StatusChip = ({ tone, children }: StatusChipProps) => {
  'use memo'

  const palette = TONES[tone]

  return (
    <span css={pillStyle(palette)} data-testid='status-chip' data-tone={tone}>
      {children}
    </span>
  )
}

const pillStyle = (palette: { fg: string; bg: string }) => css`
  ${paragraphXSmall}
  ${weightSemiBold}
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  color: ${palette.fg};
  background: ${palette.bg};

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: currentColor;
  }
`

export default StatusChip
